import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { plainToClass, plainToInstance } from 'class-transformer';
import { FaqCategoryService, FaqService, ImageConversionService } from '@core/services';
import { FaqCategoryEntity, FaqEntity } from '@core/entities';
import { GenerateImageUrlWithToken, removeNullAndUndefinedProperties } from '@core/utils';
import { SectionType } from '@core/enums';
import { Observable } from 'rxjs';

export class UseFaq {
  private readonly _faqCategoryService = inject(FaqCategoryService);
  private readonly _faqService = inject(FaqService);
  private readonly _imageConversionService = inject(ImageConversionService);

  // Faq Functions

  getFaqsByCategory(categoryId: number) {
    return this._faqService.getAll(categoryId).pipe(
      map((response: FaqEntity[]) => {
        return response.map((faq) => {
          const transformedItem = plainToClass(FaqEntity, faq);
          if (transformedItem.image) transformedItem.imageUrl = GenerateImageUrlWithToken(faq.id, SectionType.FAQ);
          return transformedItem as FaqEntity;
        });
      }),
    );
  }

  findFaqByTitle(query: string) {
    return this._faqService.find(query).pipe(
      map((response: FaqEntity[]) => {
        return response.map((faq) => {
          const transformedItem = plainToClass(FaqEntity, faq);
          if (transformedItem.image) transformedItem.imageUrl = GenerateImageUrlWithToken(faq.id, SectionType.FAQ);
          return transformedItem as FaqEntity;
        });
      }),
    );
  }

  createFaq(faq: FormData) {
    return this._faqService.create(faq);
  }

  updateFaq(id: number, faq: FormData) {
    return this._faqService.update(id, faq);
  }

  deleteFaq(faqId: number) {
    return this._faqService.delete(faqId);
  }

  downloadFaqDocument(fileName: string, faqId: number) {
    const body = {
      name: fileName,
      faq_id: faqId,
      type: 0,
    };
    return this._faqService.downloadDocument(body);
  }

  getFaqById(id: number): Observable<FaqEntity> {
    return this._faqService.get(id).pipe(
      map((response) => {
        const transformedItem = plainToInstance(FaqEntity, response);
        if (transformedItem.image) transformedItem.imageUrl = GenerateImageUrlWithToken(response.id, SectionType.FAQ);
        return transformedItem;
      }),
    );
  }

  // Faq Categories Functions

  getFaqCategories() {
    return this._faqCategoryService.find().pipe(
      map((response: FaqCategoryEntity[]) => {
        return response.map((category) => removeNullAndUndefinedProperties(plainToClass(FaqCategoryEntity, category)));
      }),
    );
  }

  createFaqCategory(faqCategory: FaqCategoryEntity) {
    return this._faqCategoryService.create(faqCategory);
  }

  updateFaqCategory(id: number, faqCategory: FaqCategoryEntity) {
    return this._faqCategoryService.update(id, faqCategory);
  }

  deleteFaqCategory(faqCategoryId: number) {
    return this._faqCategoryService.delete(faqCategoryId);
  }
}
